import React, { useState, useEffect } from 'react';
import { useAsset } from '../../context/AssetContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Monitor, CheckCircle, Shield, Users } from 'lucide-react';

const CATEGORY_LABELS = {
  asset_types: { label: 'Asset Types', icon: Monitor, description: 'Types of IT assets' },
  asset_statuses: { label: 'Asset Statuses', icon: CheckCircle, description: 'Possible asset statuses' },
  warranty_statuses: { label: 'Warranty', icon: Shield, description: 'Warranty status options' },
  employee_statuses: { label: 'Employee Status', icon: Users, description: 'Employee status options' }
};

const AssetSettings = () => {
  const { getLOVsRaw, addLOVItem, updateLOVItem, deleteLOVItem, isReady } = useAsset();

  const [loading, setLoading] = useState(true);
  const [lovData, setLovData] = useState({});
  const [activeTab, setActiveTab] = useState('asset_types');

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [selectedLOV, setSelectedLOV] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formValue, setFormValue] = useState('');
  const [formActive, setFormActive] = useState(true);

  useEffect(() => {
    if (isReady) {
      loadLOVs();
    }
  }, [isReady]);

  const loadLOVs = async () => {
    setLoading(true);
    const data = await getLOVsRaw();
    setLovData(data || {});
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!formValue.trim()) {
      toast.error('Please enter a value');
      return;
    }

    setFormLoading(true);
    const result = await addLOVItem(activeTab, formValue.trim());
    setFormLoading(false);

    if (result.success) {
      toast.success('Value added successfully');
      setAddDialogOpen(false);
      setFormValue('');
      loadLOVs();
    } else {
      toast.error(result.error);
    }
  };

  const handleEdit = async () => {
    setFormLoading(true);
    const result = await updateLOVItem(selectedLOV.id, { value: formValue, is_active: formActive });
    setFormLoading(false);

    if (result.success) {
      toast.success('Value updated successfully');
      setEditDialogOpen(false);
      setFormValue('');
      loadLOVs();
    } else {
      toast.error(result.error);
    }
  };

  const handleDelete = async () => {
    setFormLoading(true);
    const result = await deleteLOVItem(selectedLOV.id);
    setFormLoading(false);

    if (result.success) {
      toast.success('Value deleted successfully');
      setDeleteDialogOpen(false);
      loadLOVs();
    } else {
      toast.error(result.error);
    }
  };

  const openEditDialog = (lov) => {
    setSelectedLOV(lov);
    setFormValue(lov.value);
    setFormActive(lov.is_active);
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (lov) => {
    setSelectedLOV(lov);
    setDeleteDialogOpen(true);
  };

  const currentItems = lovData[activeTab] || [];
  const categoryInfo = CATEGORY_LABELS[activeTab] || { label: activeTab, description: '' };

  return (
    <div className="space-y-6" data-testid="asset-settings">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">List of Values</h2>
        <p className="text-muted-foreground">Manage dropdown options used throughout the application</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              {Object.entries(CATEGORY_LABELS).map(([key, info]) => (
                <TabsTrigger key={key} value={key} className="gap-2">
                  <info.icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{info.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {Object.keys(CATEGORY_LABELS).map((category) => (
              <TabsContent key={category} value={category} className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold">{CATEGORY_LABELS[category].label}</h3>
                    <p className="text-sm text-muted-foreground">{CATEGORY_LABELS[category].description}</p>
                  </div>
                  <Button onClick={() => { setFormValue(''); setAddDialogOpen(true); }}>
                    <Plus className="mr-2 h-4 w-4" /> Add Value
                  </Button>
                </div>

                {loading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                  </div>
                ) : (lovData[category] || []).length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground border rounded-lg">
                    No values configured. Add your first value.
                  </div>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Value</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="w-[100px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(lovData[category] || []).map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.value}</TableCell>
                            <TableCell>
                              <Badge variant={item.is_active ? 'default' : 'secondary'}>
                                {item.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" onClick={() => openEditDialog(item)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => openDeleteDialog(item)} className="text-red-600 hover:text-red-700">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Value</DialogTitle>
            <DialogDescription>Add a new option to {categoryInfo.label}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Input value={categoryInfo.label} disabled />
            </div>
            <div className="space-y-2">
              <Label>Value *</Label>
              <Input 
                value={formValue} 
                onChange={(e) => setFormValue(e.target.value)} 
                placeholder="Enter value"
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={formLoading}>
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Value
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Value</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Value</Label>
              <Input value={formValue} onChange={(e) => setFormValue(e.target.value)} />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={formActive} onCheckedChange={setFormActive} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEdit} disabled={formLoading}>
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Value</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedLOV?.value}"? 
              Consider deactivating instead if this value is in use.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={formLoading}>
              {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetSettings;
